import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Gift } from "lucide-react";
import TransitionBlock3 from "./TransitionBlock3";

export default function PricingBlock() {
	const { t } = useTranslation();

	const features = t("pricing.features", { returnObjects: true }) || [];

	return (
		<section
			id="precios"
			className="relative bg-gradient-to-b from-white via-primary/5 to-primary/10 pb-20"
		>
			{/* Transición decorativa superior */}
			<TransitionBlock3 />

			<div className="relative max-w-5xl mx-auto px-6 pt-20 md:pt-32">
				{/* Header */}
				<div className="text-center mb-12">
					<motion.span
						className="inline-block text-primary text-sm font-semibold uppercase tracking-wider mb-4"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						viewport={{ once: true }}
					>
						{t("pricing.badge")}
					</motion.span>
					<motion.h2
						className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.1 }}
						viewport={{ once: true }}
					>
						{t("pricing.title")}
					</motion.h2>
					<motion.p
						className="text-lg text-gray-600 max-w-2xl mx-auto"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
					>
						{t("pricing.description")}
					</motion.p>
				</div>

				{/* Pricing Card */}
				<motion.div
					className="max-w-lg mx-auto"
					initial={{ opacity: 0, y: 40 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, delay: 0.2 }}
					viewport={{ once: true }}
				>
					<div className="relative bg-white rounded-3xl shadow-2xl shadow-primary/10 overflow-hidden border border-primary/10">
						{/* Trial Badge */}
						<div className="absolute -right-12 top-8 rotate-45 bg-primary text-white text-xs font-bold px-12 py-2 shadow-lg">
							{t("pricing.trialBadge")}
						</div>

						<div className="p-8 md:p-10">
							{/* Price */}
							<div className="mb-6">
								<div className="flex items-baseline gap-2 justify-center">
									<span className="text-gray-500 text-xl font-medium">
										{t("pricing.fromText")}
									</span>
									<span className="text-5xl md:text-6xl font-extrabold text-gray-900">
										{t("pricing.price")}
									</span>
									<span className="text-gray-500 text-lg">
										{t("pricing.period")}
									</span>
								</div>
								<p className="text-sm text-gray-500 mt-2 text-center">
									{t("pricing.vatNote")}
								</p>
							</div>

							{/* Trial highlight */}
							<div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-4 mb-6 flex items-center gap-3">
								<Gift className="w-8 h-8 text-primary flex-shrink-0" />
								<div>
									<p className="font-semibold text-gray-900">
										{t("pricing.trialTitle")}
									</p>
									<p className="text-sm text-gray-600">
										{t("pricing.trialText")}
									</p>
								</div>
							</div>

							{/* Features */}
							<div className="space-y-3 mb-8">
								{features.map((feature, i) => (
									<div
										key={i}
										className="flex items-start gap-3 text-gray-700"
									>
										<Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
										<span className="text-sm md:text-base">{feature}</span>
									</div>
								))}
							</div>

							{/* CTA */}
							<motion.a
								href="#sectores"
								onClick={(e) => {
									e.preventDefault();
									document
										.querySelector("#sectores")
										?.scrollIntoView({ behavior: "smooth" });
								}}
								whileHover={{ scale: 1.02 }}
								whileTap={{ scale: 0.98 }}
								className="w-full flex items-center justify-center gap-2 bg-primary text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
							>
								{t("pricing.button")}
								<ArrowRight className="w-5 h-5" />
							</motion.a>

							<p className="text-center text-sm text-gray-500 mt-4">
								{t("pricing.noCommitment")}
							</p>
						</div>
					</div>
				</motion.div>

				{/* Sector pricing note */}
				<motion.div
					className="text-center mt-10"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.4 }}
					viewport={{ once: true }}
				>
					<a
						href="#sectores"
						onClick={(e) => {
							e.preventDefault();
							document
								.querySelector("#sectores")
								?.scrollIntoView({ behavior: "smooth" });
						}}
						className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
					>
						{t("pricing.sectorNote")}
						<ArrowRight className="w-4 h-4" />
					</a>
				</motion.div>
			</div>
		</section>
	);
}
